import React, { useState, useMemo, useEffect } from 'react';
import { find } from 'lodash';
import { avUserApi } from '@availity/api-axios';
import { Box } from '@availity/element';
import { useEffectAsync } from '@availity/hooks';

import { useAppContext } from '@/context';
import { checkNavigation, getHiddenValueCallbackById, JOURNEY_UPDATE_PASSWORD } from '@/global';
import { useFRStep } from '@/hooks';
import {
  EmailSent,
  EnterCurrentPassword,
  ErrorPage,
  PasswordReset,
  UpdatePasswordConfirmation,
  LoginStart,
  TwoFAChallengeSelectMethod,
  TwoFAConfirmation,
  TwoFAConfirmNumber,
  TwoFAEnrollmentSelectMethod,
  TwoFAHardTokenEnrollment,
  TwoFAHotpEnrollment,
  TwoFAHotpInputToken,
  TwoFAInputBackupCode,
  TwoFAStart,
  TwoFATotpEnrollment,
  TwoFATotpInputToken,
} from '@/pages';

const forceAuth = true;

const UpdatePassword = () => {
  const { queryParams = {}, userApiResponse, setUserApiResponse } = useAppContext();
  const [currentStep, setCurrentStep] = useState();

  const { authIndexValue = '' } = queryParams;

  // process authIndexValue in event that the UI should navigate to a different journey
  const journeyId = useMemo(() => authIndexValue?.toLowerCase(), [authIndexValue]);

  // call utility function to check if we need to navigate to a different route
  useEffect(() => {
    if (journeyId) {
      checkNavigation(journeyId);
    }
  }, [journeyId]);

  // get FR step info from custom hook
  // NOTE: updating the currentStep 'should' trigger fetching of the stepToRender
  // currentStep should be changed on form submission
  const { stepToRender, errorCode } = useFRStep(currentStep, JOURNEY_UPDATE_PASSWORD, forceAuth);

  // check to see if we have a session to avoid unnecessary calls to user api
  const hasSessionCookie = useMemo(() => {
    let result = false;

    if (stepToRender) {
      result = document.cookie.indexOf('XSRF-TOKEN') >= 0;
    }

    return result;
  }, [stepToRender]);

  // if we have a session, need to get the current user - user id is needed for the password reset screen
  useEffectAsync(async () => {
    if (hasSessionCookie && !userApiResponse) {
      try {
        const apiResponse = await avUserApi.me();
        setUserApiResponse({ ...apiResponse });
      } catch (error) {
        if (error) console.log('Unable to get current user.');
      }
    }
  }, [hasSessionCookie]);

  const pageNodeToRender = useMemo(() => {
    let component;

    if (stepToRender) {
      const { payload = {} } = stepToRender;
      const { callbacks = [], stage } = payload;

      // first check for a suspended text output callback; in this case the email sent node should render
      // a stage name should not be present in this case
      if (callbacks?.length > 0) {
        const callbackToCheck = callbacks[0];

        if (callbackToCheck?.type?.toLowerCase() === 'suspendedtextoutputcallback') {
          component = <EmailSent key="emailSent" stepToRender={stepToRender} />;
        }
      }

      // for 2fa journeys; determine totp type
      // look for hidden value callback to get totp type if needed
      const totpTypeCallback = getHiddenValueCallbackById(callbacks, 'totpdevicetype');
      const totpTypeCallbackOutput = totpTypeCallback?.output;
      const totpType =
        totpTypeCallbackOutput &&
        find(totpTypeCallbackOutput, (outputObj) => outputObj?.name?.toLowerCase() === 'value')?.value;

      switch (stage) {
        case 'LoginpageUnique001': {
          component = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'UpdatePassword-EnterCurrentPassword': {
          component = (
            <EnterCurrentPassword stepToRender={stepToRender} setCurrentStep={setCurrentStep} errorCode={errorCode} />
          );
          break;
        }
        case 'UpdatePassword-NewPassword': {
          component = (
            <PasswordReset stepToRender={stepToRender} setCurrentStep={setCurrentStep} errorCode={errorCode} />
          );
          break;
        }
        case 'UpdatePassword-Confirmation': {
          component = (
            <UpdatePasswordConfirmation
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
            />
          );
          break;
        }
        case 'MFAEnrollment-Welcome': {
          component = <TwoFAStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-SelectMethod': {
          component = <TwoFAEnrollmentSelectMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-OATHAppRegister': {
          component =
            totpType?.toLowerCase() === 'authenticatorapp' ? (
              <TwoFATotpEnrollment stepToRender={stepToRender} setCurrentStep={setCurrentStep} />
            ) : (
              <TwoFAHardTokenEnrollment stepToRender={stepToRender} setCurrentStep={setCurrentStep} />
            );
          break;
        }
        case 'MFAEnrollment-AppOATHVerify':
        case 'MFA-AppOATHVerify': {
          component = <TwoFATotpInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-EnterSMSNumber': {
          component = (
            <TwoFAHotpEnrollment
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
              hotpType="sms"
            />
          );
          break;
        }
        case 'MFAEnrollment-EnterTTSNumber': {
          component = (
            <TwoFAHotpEnrollment
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
              hotpType="tts"
            />
          );
          break;
        }
        case 'MFA-SelectMethod': {
          component = <TwoFAChallengeSelectMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-ConfirmSMSNumber': {
          component = <TwoFAConfirmNumber stepToRender={stepToRender} setCurrentStep={setCurrentStep} hotpType="sms" />;
          break;
        }
        case 'MFAEnrollment-ConfirmTTSNumber': {
          component = <TwoFAConfirmNumber stepToRender={stepToRender} setCurrentStep={setCurrentStep} hotpType="tts" />;
          break;
        }
        case 'MFAEnrollment-SMSOTPVerify':
        case 'MFAEnrollment-TTSOTPVerify':
        case 'MFA-SMSOTPVerify':
        case 'MFA-TTSOTPVerify': {
          component = <TwoFAHotpInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFA-BackupVerify':
        case 'MFAEnrollment-BackupVerify': {
          component = <TwoFAInputBackupCode stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-CompleteWithCodes':
        case 'MFA-CompleteWithCodes': {
          component = <TwoFAConfirmation stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes />;
          break;
        }
        case 'MFAEnrollment-CompleteWithoutCodes':
        case 'MFA-CompleteWithoutCodes': {
          component = (
            <TwoFAConfirmation stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes={false} />
          );
          break;
        }
        case 'ErrorPage': {
          component = <ErrorPage key="errorPage" />;
          break;
        }
        default:
      }
    }

    return component;
  }, [stepToRender]);

  return <Box role="main">{pageNodeToRender}</Box>;
};

export default UpdatePassword;
